import React, { useEffect, useState } from 'react'
import { EmailInput } from '../../../../../../../shared/components/email-input/EmailInput'
import { ESignupFormSteps } from '../../../../../../../types/enums/ESignupFormSteps'
import Checkbox from '../../../../../../../shared/components/checkbox/Checkbox'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../store/store'
import {
  resetUser,
  updateUser,
} from '../../../../../../../store/reducers/userReducer'
import { ProgressBar } from 'shared/components/progress-bar/ProgressBar'
import { IUser } from 'types/interfaces/IUser'
import { InputsWrapper } from '../../style'
import { useTranslation } from 'react-i18next'
import {
  setAuthFormTexts,
  setForm,
  setIsLayoutLoading,
} from 'store/reducers/layoutReducer'
import { EExternalLinks } from 'types/enums/EExternalLinks'
import { useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { Button } from 'shared/components/button/Button'

type VerificationOptionFormData = {
  email: string
  terms: boolean
  newsletter: boolean
}
type VerificationOptionFormErrors = Partial<
  Record<keyof VerificationOptionFormData, boolean>
>

type Props = {
  baseUrl?: string
}

export const SignupFormVerificationOption = ({ baseUrl = '' }: Props) => {
  const user = useAppSelector<IUser>((state) => state.user)
  const dispatch = useAppDispatch()
  const [
    signupFormVerificationOptionData,
    setSignupFormVerificationOptionData,
  ] = useState<VerificationOptionFormData>({
    email: user.email || '',
    terms: user.terms || false,
    newsletter: false,
  })
  const [errors, setErrors] = useState<VerificationOptionFormErrors>({
    email: false,
    terms: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)

  const { t } = useTranslation('', {
    keyPrefix: `pages.signUp.verificationOption`,
  })

  useEffect(() => {
    dispatch(
      setAuthFormTexts({
        title: t('email.title'),
        subtitle: t('email.subtitle'),
      }),
    )
  }, [t])

  useEffect(() => {
    if (
      signupFormVerificationOptionData.email &&
      signupFormVerificationOptionData.terms &&
      !errors.email &&
      !errors.terms
    ) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [signupFormVerificationOptionData])

  const handleFieldChange = <
    T extends keyof typeof signupFormVerificationOptionData,
  >(
    field: T,
    value?: (typeof signupFormVerificationOptionData)[T],
    error?: boolean,
  ) => {
    setErrors({ ...errors, [field]: error })
    setSignupFormVerificationOptionData({
      ...signupFormVerificationOptionData,
      [field]: value,
    })
  }

  const nextButtonClicked = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()

    const { email, terms, newsletter } = signupFormVerificationOptionData

    dispatch(setIsLayoutLoading(true))

    try {
      dispatch(
        updateUser({
          email,
          terms,
          newsletter,
          personalDetails: { phoneNum: undefined },
        }),
      )
      dispatch(setForm({ formStep: ESignupFormSteps.password }))
    } catch (error) {
      console.error('Error:', error)
      showError()
    } finally {
      dispatch(setIsLayoutLoading(false))
    }
  }

  const showError = () => {
    setIsDisabled(true)
  }

  const navigate = useNavigate()

  const resetForm = () => {
    dispatch(resetUser())
    navigate(baseUrl === 'fanza' ? EAppRoutes.FANZA : EAppRoutes.LOGIN)
  }

  return (
    <>
      <InputsWrapper>
        <EmailInput
          labelText={t('inputs.email.labelText')}
          errorText={t('inputs.email.errorText')}
          hasError={errors.email}
          value={signupFormVerificationOptionData.email}
          callback={(value, error) => handleFieldChange('email', value, error)}
        />
      </InputsWrapper>
      <Checkbox
        hasError={errors.terms}
        callback={() =>
          handleFieldChange(
            'terms',
            !signupFormVerificationOptionData.terms,
            signupFormVerificationOptionData.terms,
          )
        }
        id={'terms'}
        label={t('inputs.terms.labelText')}
        errorText={t('inputs.terms.errorText')}
        linkLabel={t('inputs.terms.linkLabel')}
        href={EExternalLinks.HJBC_TERMS}
        checked={signupFormVerificationOptionData.terms}
      ></Checkbox>
      <Checkbox
        callback={() =>
          handleFieldChange(
            'newsletter',
            !signupFormVerificationOptionData.newsletter,
            signupFormVerificationOptionData.newsletter,
          )
        }
        id={'newsletter'}
        label={t('inputs.newsletter.labelText')}
        checked={signupFormVerificationOptionData.newsletter}
      ></Checkbox>
      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.next.text')}
        callback={async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          await nextButtonClicked(e)
        }
      />
      <Button
        type="reset"
        disabled={false}
        className={`button`}
        text={t('buttons.back.text')}
        callback={resetForm}
      />
      <ProgressBar progress={10} />
    </>
  )
}
